import { useState } from 'react';
import api from 'api';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { breakpoints } from 'styles';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Owners,
  Pagination,
  TitleWithIcon
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { PathAccessGroupsAdd, PathAccessGroupsRemove } from '.';

import type { EmailTemplateData } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
}

const PathGroups = ({ emailTemplates, emailTemplatesDefault }: Props) => {
  const {
    messages,
    pageState: { path },
    serverConfig: { userUseName },
    user
  } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading, updateData } = useApiData({
    url: `/admin/paths/${path.id}/groups?page=${page}&limit=10`
  });

  const addedGroupIds = data?.addedGroupIds || [];
  const groups = data?.groups || [];
  const pages = data?.pages || 0;

  const { data: administeredGroupsData } = useApiData({
    url: '/admin/groups-available'
  });

  const administeredGroups = administeredGroupsData?.groups || [];

  const filteredAdministeredGroups =
    administeredGroups && addedGroupIds
      ? administeredGroups.filter((group) => !addedGroupIds.includes(group.id))
      : null;

  return (
    <div className="groups">
      <Box
        headerContent={
          <PathAccessGroupsAdd
            administeredGroups={filteredAdministeredGroups}
            emailTemplates={emailTemplates}
            emailTemplatesDefault={emailTemplatesDefault}
            updateGroups={updateData}
          />
        }
        loading={loading}
        padding={0}
        title={messages.commonGroups}
      >
        {groups && groups.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonGroup}</BoxTableCell>
              <BoxTableCell>{messages.commonAdded}</BoxTableCell>
              <BoxTableCell>{messages.groupsAddedUsers}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {groups.map((group) => (
              <BoxTableRow key={group.id}>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonGroup}
                >
                  <TitleWithIcon
                    icon="users"
                    title={group.title}
                    titleLink={{
                      as: `/groups/${group.id}`,
                      href: '/groups/[groupId]'
                    }}
                  >
                    <Owners owners={group.owners} />
                  </TitleWithIcon>
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonAdded}
                >
                  <DisplayFormattedDate date={group.addedDate} />
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.groupsAddedUsers}
                >
                  {group.numberOfUsers + group.numberOfAttributeUsers}
                </BoxTableCell>
                <BoxTableCell
                  shrink={true}
                  verticalBreakpoint={breakpoints.medium}
                >
                  <MoreOptions>
                    <PathAccessGroupsRemove
                      updateGroups={updateData}
                      groupId={group.id}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator icon="users" title={messages.groupsEmpty} />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={pages}
        onPageChange={({ page }) => setPage(page)}
      />
    </div>
  );
};

export default PathGroups;
